import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { bucketFor, scopeParams } from './query';
import {
  ErrorEvent,
  ErrorName,
  ErrorTimePoint,
  MetricPage,
  OverviewMetrics,
  OverviewPoint,
  RybbitScope,
} from './types';

export class RybbitApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export interface RybbitApi {
  getOverview(site: string, scope: RybbitScope): Promise<OverviewMetrics>;
  getOverviewTimeSeries(
    site: string,
    scope: RybbitScope,
  ): Promise<OverviewPoint[]>;
  getMetric(
    site: string,
    parameter: string,
    scope: RybbitScope,
    limit?: number,
  ): Promise<MetricPage>;
  getLiveUserCount(site: string, minutes?: number): Promise<number>;
  getErrorNames(
    site: string,
    scope: RybbitScope,
    limit?: number,
  ): Promise<ErrorName[]>;
  getErrorEvents(
    site: string,
    errorMessage: string,
    scope: RybbitScope,
    limit?: number,
  ): Promise<ErrorEvent[]>;
  getErrorTimeSeries(
    site: string,
    errorMessage: string,
    scope: RybbitScope,
  ): Promise<ErrorTimePoint[]>;
}

export const rybbitApiRef = createApiRef<RybbitApi>({
  id: 'plugin.rybbit.api',
});

/**
 * Reads the Rybbit API through the Backstage proxy (`proxy.endpoints./rybbit`).
 *
 * The API key lives in app-config and is attached by the proxy, so nothing
 * secret reaches the browser. The proxy also strips the caller's Authorization
 * header before forwarding, so the Backstage token never leaves the backend.
 *
 * Every method unwraps one of Rybbit's envelopes — `{data}`, `{data:{data,
 * totalCount}}`, `{count}`, `{success,data}` — so components see a flat shape
 * and only this file knows which endpoint wraps what.
 */
export class RybbitClient implements RybbitApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
    private readonly proxyPath: string = '/rybbit',
  ) {}

  private async get<T>(
    path: string,
    params: Record<string, string>,
  ): Promise<T> {
    const base = await this.discoveryApi.getBaseUrl('proxy');
    const search = new URLSearchParams(params).toString();
    const response = await this.fetchApi.fetch(
      `${base}${this.proxyPath}${path}${search ? `?${search}` : ''}`,
    );
    if (!response.ok) {
      let body: unknown;
      try {
        body = await response.json();
      } catch {
        body = await response.text().catch(() => '');
      }
      const message =
        (typeof body === 'object' &&
        body &&
        typeof (body as any).error === 'string'
          ? (body as any).error
          : undefined) ?? `${response.status} ${response.statusText}`;
      throw new RybbitApiError(message, response.status, body);
    }
    return response.json();
  }

  private static site(site: string): string {
    return `/sites/${encodeURIComponent(site)}`;
  }

  async getOverview(
    site: string,
    scope: RybbitScope,
  ): Promise<OverviewMetrics> {
    const raw = await this.get<{ data?: Partial<OverviewMetrics> }>(
      `${RybbitClient.site(site)}/overview`,
      scopeParams(scope),
    );
    const d = raw?.data ?? {};
    return {
      sessions: d.sessions ?? 0,
      pageviews: d.pageviews ?? 0,
      users: d.users ?? 0,
      pages_per_session: d.pages_per_session ?? 0,
      bounce_rate: d.bounce_rate ?? 0,
      session_duration: d.session_duration ?? 0,
    };
  }

  /** Same six figures as the overview, one row per bucket. */
  async getOverviewTimeSeries(
    site: string,
    scope: RybbitScope,
  ): Promise<OverviewPoint[]> {
    const raw = await this.get<{ data?: unknown }>(
      `${RybbitClient.site(site)}/overview/time-series`,
      { ...scopeParams(scope), bucket: bucketFor(scope.range) },
    );
    return Array.isArray(raw?.data) ? (raw.data as OverviewPoint[]) : [];
  }

  /** Double-wrapped upstream: `{ data: { data: [], totalCount } }`. */
  async getMetric(
    site: string,
    parameter: string,
    scope: RybbitScope,
    limit = 10,
  ): Promise<MetricPage> {
    const raw = await this.get<{
      data?: { data?: unknown; totalCount?: number };
    }>(`${RybbitClient.site(site)}/metric`, {
      ...scopeParams(scope),
      parameter,
      limit: String(limit),
    });
    const items = Array.isArray(raw?.data?.data) ? raw.data!.data! : [];
    return {
      items: items as MetricPage['items'],
      totalCount: raw?.data?.totalCount ?? items.length,
    };
  }

  /** The one endpoint with no envelope and no time params. */
  async getLiveUserCount(site: string, minutes = 5): Promise<number> {
    const raw = await this.get<{ count?: number }>(
      `${RybbitClient.site(site)}/live-user-count`,
      { minutes: String(minutes) },
    );
    return raw?.count ?? 0;
  }

  async getErrorNames(
    site: string,
    scope: RybbitScope,
    limit = 25,
  ): Promise<ErrorName[]> {
    const raw = await this.get<{ data?: unknown }>(
      `${RybbitClient.site(site)}/errors/names`,
      { ...scopeParams(scope), limit: String(limit) },
    );
    return Array.isArray(raw?.data) ? (raw.data as ErrorName[]) : [];
  }

  async getErrorEvents(
    site: string,
    errorMessage: string,
    scope: RybbitScope,
    limit = 20,
  ): Promise<ErrorEvent[]> {
    const raw = await this.get<{ data?: unknown }>(
      `${RybbitClient.site(site)}/errors/events`,
      { ...scopeParams(scope), errorMessage, limit: String(limit) },
    );
    return Array.isArray(raw?.data) ? (raw.data as ErrorEvent[]) : [];
  }

  async getErrorTimeSeries(
    site: string,
    errorMessage: string,
    scope: RybbitScope,
  ): Promise<ErrorTimePoint[]> {
    const raw = await this.get<{ success?: boolean; data?: unknown }>(
      `${RybbitClient.site(site)}/errors/time-series`,
      {
        ...scopeParams(scope),
        errorMessage,
        bucket: bucketFor(scope.range),
      },
    );
    return Array.isArray(raw?.data) ? (raw.data as ErrorTimePoint[]) : [];
  }
}
